class FacetShort extends HTMLSelectElement {
  constructor() {
    super();
    this.initMotionInView();
    this.addEventListener('change', this.onSelectChange.bind(this));
  }

  get options() {
    return this.getElementsByTagName('option');
  }

  get fakeSelectElement() {
    return this.previousElementSibling;
  }

  initMotionInView() {
    FoxTheme.Motion.inView(this, this.calcSelectWidth.bind(this), { margin: '200px 0px 200px 0px' });
  }

  calcSelectWidth() {
    const selectStyle = window.getComputedStyle(this);
    const selectedOptionText = this.options[this.selectedIndex].text;

    const textElement = this.createTextElement(selectedOptionText, selectStyle);
    document.body.appendChild(textElement);
    const selectWidth = textElement.offsetWidth;
    textElement.remove();

    this.style.setProperty('--width', `${selectWidth + 22}px`);
  }

  createTextElement(text, style) {
    const textElement = document.createElement('span');
    textElement.style.fontFamily = style.fontFamily;
    textElement.style.fontSize = style.fontSize;
    textElement.style.fontWeight = style.fontWeight;
    textElement.style.visibility = 'hidden';
    textElement.style.position = 'absolute';
    textElement.innerHTML = text;
    return textElement;
  }

  onSelectChange(event) {
    this.calcSelectWidth();
    this.updateFormParams(event);
  }

  updateFormParams(event) {
    const form = this.getClosestFacetForm() || this.getFirstFacetForm();

    if (form) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', event.target.value);
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(form));
      url.searchParams.delete('page');
      form.renderSection(url.toString(), event);
    }
  }

  getClosestFacetForm() {
    return this.closest('form[is="facet-form"]');
  }

  getFirstFacetForm() {
    return document.querySelector('form[is="facet-form"]');
  }
}
customElements.define('facet-short', FacetShort, { extends: 'select' });

class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.minRangeInput = this.querySelector('input[type="range"]:first-child');
    this.maxRangeInput = this.querySelector('input[type="range"]:last-child');
    this.minPriceInput = this.querySelector('input[name="filter.v.price.gte"]');
    this.maxPriceInput = this.querySelector('input[name="filter.v.price.lte"]');

    /* WWC: no-op when the price group is missing parts (e.g. every result is price on request, range 0) */
    if (!this.minPriceInput || !this.maxPriceInput) return;
    this.minPriceInput.addEventListener('focus', this.minPriceInput.select);
    this.maxPriceInput.addEventListener('focus', this.maxPriceInput.select);
    this.minPriceInput.addEventListener('change', this.handleInputMinChange.bind(this));
    this.maxPriceInput.addEventListener('change', this.handleInputMaxChange.bind(this));

    if (!this.hasRange()) return;
    /* WWC D74 slider: live £ labels, number inputs synced both ways, one debounced submit after release */
    this.wwcLabels();
    this.minPriceInput.addEventListener('input', this.wwcSyncFromNumber.bind(this));
    this.maxPriceInput.addEventListener('input', this.wwcSyncFromNumber.bind(this));
    this.minRangeInput.addEventListener('change', this.handleRangeMinChange.bind(this));
    this.maxRangeInput.addEventListener('change', this.handleRangeMaxChange.bind(this));
    this.minRangeInput.addEventListener('input', this.handleRangeMinInput.bind(this));
    this.maxRangeInput.addEventListener('input', this.handleRangeMaxInput.bind(this));
  }

  wwcLabels() {
    const wrap = this.minRangeInput.parentElement;
    let el = this.querySelector('.wwc-pr-labels');
    if (!el) {
      el = document.createElement('div');
      el.className = 'wwc-pr-labels';
      el.setAttribute('aria-hidden', 'true');
      el.innerHTML = '<span data-wwc-pr-min></span><span data-wwc-pr-max></span>';
      wrap.parentNode.insertBefore(el, wrap);
    }
    this.wwcLabelMin = el.querySelector('[data-wwc-pr-min]');
    this.wwcLabelMax = el.querySelector('[data-wwc-pr-max]');
    const sym = (this.querySelector('.facet-range__symbol') || {}).textContent || '£';
    this.wwcSym = sym.trim() || '£';
    this.wwcLast = (this.minPriceInput.value || '') + '|' + (this.maxPriceInput.value || '');
    this.wwcPaint();
  }

  wwcPaint() {
    if (!this.wwcLabelMin) return;
    const max = parseInt(this.maxRangeInput.max) || 0;
    const lo = parseInt(this.minRangeInput.value) || 0;
    const hi = parseInt(this.maxRangeInput.value);
    const h = isNaN(hi) ? max : hi;
    this.wwcLabelMin.textContent = this.wwcSym + lo;
    this.wwcLabelMax.textContent = this.wwcSym + h;
    const wrap = this.minRangeInput.parentElement;
    if (max > 0) {
      wrap.style.setProperty('--range-min', `${(lo / max) * 100}%`);
      wrap.style.setProperty('--range-max', `${(h / max) * 100}%`);
    }
    this.minRangeInput.setAttribute('aria-valuetext', this.wwcSym + lo);
    this.maxRangeInput.setAttribute('aria-valuetext', this.wwcSym + h);
  }

  wwcSyncFromNumber(event) {
    const v = parseInt(event.target.value);
    const max = parseInt(this.maxRangeInput.max) || 0;
    if (event.target === this.minPriceInput) this.minRangeInput.value = isNaN(v) ? 0 : Math.max(0, Math.min(v, max));
    else this.maxRangeInput.value = isNaN(v) ? max : Math.max(0, Math.min(v, max));
    this.wwcPaint();
  }

  wwcSubmitSoon(input) {
    clearTimeout(this.wwcTimer);
    this.wwcTimer = setTimeout(() => {
      const key = (this.minPriceInput.value || '') + '|' + (this.maxPriceInput.value || '');
      if (key === this.wwcLast) return; /* nothing changed since the last request: no double submit */
      this.wwcLast = key;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }, 400);
  }

  hasRange() {
    return !!(this.minRangeInput && this.maxRangeInput && this.minRangeInput !== this.maxRangeInput &&
      parseFloat(this.maxRangeInput.max) > 0);
  }

  handleInputMinChange(event) {
    event.preventDefault();
    event.target.value = Math.max(
      Math.min(parseInt(event.target.value), parseInt(this.maxPriceInput.value || event.target.max) - 1),
      event.target.min
    );
    if (!this.hasRange()) return;
    this.minRangeInput.value = event.target.value;
    this.minRangeInput.parentElement.style.setProperty(
      '--range-min',
      `${(parseInt(this.minRangeInput.value) / parseInt(this.minRangeInput.max)) * 100}%`
    );
    this.wwcPaint();
    this.wwcLast = (this.minPriceInput.value || '') + '|' + (this.maxPriceInput.value || '');
  }

  handleInputMaxChange(event) {
    event.preventDefault();
    event.target.value = Math.min(
      Math.max(parseInt(event.target.value), parseInt(this.minPriceInput.value || event.target.min) + 1),
      event.target.max
    );
    if (!this.hasRange()) return;
    this.maxRangeInput.value = event.target.value;
    this.maxRangeInput.parentElement.style.setProperty(
      '--range-max',
      `${(parseInt(this.maxRangeInput.value) / parseInt(this.maxRangeInput.max)) * 100}%`
    );
    this.wwcPaint();
    this.wwcLast = (this.minPriceInput.value || '') + '|' + (this.maxPriceInput.value || '');
  }

  handleRangeMinChange(event) {
    event.stopPropagation();
    this.minPriceInput.value = event.target.value;
    this.wwcPaint();
    this.wwcSubmitSoon(this.minPriceInput);
  }

  handleRangeMaxChange(event) {
    event.stopPropagation();
    this.maxPriceInput.value = event.target.value;
    this.wwcPaint();
    this.wwcSubmitSoon(this.maxPriceInput);
  }

  handleRangeMinInput(event) {
    event.target.value = Math.min(
      parseInt(event.target.value),
      parseInt(this.maxPriceInput.value || event.target.max) - 1
    );
    event.target.parentElement.style.setProperty(
      '--range-min',
      `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`
    );
    this.minPriceInput.value = event.target.value;
    this.wwcPaint();
  }

  handleRangeMaxInput(event) {
    event.target.value = Math.max(
      parseInt(event.target.value),
      parseInt(this.minPriceInput.value || event.target.min) + 1
    );
    event.target.parentElement.style.setProperty(
      '--range-max',
      `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`
    );
    this.maxPriceInput.value = event.target.value;
    this.wwcPaint();
  }
}
customElements.define('price-range', PriceRange);

class FacetForm extends HTMLFormElement {
  constructor() {
    super();

    this.dirty = false;
    this.cachedMap = new Map();

    this.addEventListener('change', this.onFormChange);
    this.addEventListener('submit', this.onFormSubmit);
  }

  onFormChange() {
    this.dirty = true;
    this.dispatchEvent(new Event('submit', { cancelable: true }));
  }

  onFormSubmit(event) {
    event.preventDefault();
    if (!this.dirty) return;

    const url = this.buildUrl().toString();
    this.renderSection(url, event);
  }

  buildUrl() {
    const searchParams = new URLSearchParams(new FormData(this));
    const url = new URL(this.action);

    url.search = '';
    searchParams.forEach((value, key) => url.searchParams.append(key, value));

    ['page', 'filter.v.price.gte', 'filter.v.price.lte'].forEach((item) => {
      if (url.searchParams.get(item) === '') {
        url.searchParams.delete(item);
      }
    });

    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url;
  }

  updateURLHash(url) {
    const clonedUrl = new URL(url);
    clonedUrl.searchParams.delete('section_id');
    history.replaceState({}, '', clonedUrl.toString());
  }

  beforeRenderSection() {
    const container = document.getElementById('ProductGridContainer');
    const loadings = document.querySelectorAll('[data-facet-loading]');
    const translateY = FoxTheme.config.motionReduced ? 0 : 50;

    FoxTheme.Motion.timeline([[container, { y: translateY, opacity: 0 }, { duration: 0 }]]);

    // WWC D115 (Colin 30 Oct 20:50): desktop sticky filter sidebar (>=1280px) - keep the page where it is on a filter/sort
    // change (afterRenderSection only scrolls if the new results end above the screen). Was: always smooth-scroll the window to
    // .collection - 200 on every change, which read as "the page jumps back to the top" while working down the sidebar.
    // Mobile drawer / horizontal filters keep the original scroll-to-results.
    const wwcSide = document.getElementById('FacetFiltersContainer');
    this.wwcKeepScroll = !!(wwcSide && wwcSide.offsetParent !== null && window.matchMedia('(min-width: 1280px)').matches);
    // D116 (Colin 31 Oct 09:07 "watch the filter glitch if it jumps to the top"): no scroll here any more. Below 1280px (filter
    // drawer, mobile) this used to smooth-scroll the window to .collection - 200 (above the toolbar, often into the banner) 100ms
    // after every filter/sort tick, i.e. the page ran back up while the drawer was still open. Now afterRenderSection brings the
    // TOP OF THE PRODUCT GRID to just under the sticky header/toolbar, and only when the visitor had scrolled past it.
    // The grid's height is held while the results are swapped so the page can't shrink under the visitor (scroll clamping).
    if (container && !container.style.minHeight) {
      container.style.minHeight = container.offsetHeight + 'px';
      this.wwcHeld = true;
    }

    setTimeout(() => {
      if (loadings) {
        loadings.forEach((loading) => {
          loading.classList.add('btn--loading');
        });
      }
    }, 100);
  }

  afterRenderSection() {
    const container = document.getElementById('ProductGridContainer');
    const items = container.querySelectorAll('.product-card');
    const loadings = document.querySelectorAll('[data-facet-loading]');
    const translateY = FoxTheme.config.motionReduced ? 0 : 50;

    FoxTheme.Motion.timeline([
      [container, { y: [translateY, 0], opacity: [0, 1] }],
      [
        items,
        { y: [translateY, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] },
        { duration: 0.5, delay: FoxTheme.config.motionReduced ? 0 : FoxTheme.Motion.stagger(0.1) }
      ]
    ]);

    if (loadings) {
      loadings.forEach((loading) => {
        loading.classList.remove('btn--loading');
      });
    }

    this.wwcPlaceResults(container);

    document.dispatchEvent(new CustomEvent('collection:rerendered'));
  }

  /* D116: where the results land after a filter / sort change (see beforeRenderSection).
     - Visitor still above the grid (grid top visible): nothing moves.
     - Desktop sidebar (>= 1280px): keep the position (D115), unless the new results now end above the screen.
     - Otherwise (drawer, mobile, < 1280px): bring the top of the results (active-filter chips, else the grid) to just under the
       sticky header + toolbar - smooth when visible, instant while the filter drawer still covers the page.
     The held height is released once that scroll is done, so nothing below the visitor jumps. */
  wwcStickyBottom() {
    let b = 0;
    document.querySelectorAll('.header-section, .collection .sticky-element.z-9, [data-wwc-plpbar]:not(.is-hidden)').forEach((el) => {
      const cs = getComputedStyle(el);
      if (cs.position !== 'sticky' && cs.position !== 'fixed') return;
      const r = el.getBoundingClientRect();
      // only bars that are stuck at the top of the screen right now
      if (!(r.height > 0 && r.top <= (parseFloat(cs.top) || 0) + 1)) return;
      // the header box stays full height but its nav row slides up under the top row once scrolled: use the visible rows
      const parts = el.classList.contains('header-section') ? el.querySelectorAll('.header__top, .header__bottom') : [];
      let bottom = r.bottom;
      if (parts.length) {
        bottom = 0;
        parts.forEach((p) => {
          if (getComputedStyle(p).visibility === 'hidden') return;
          const pr = p.getBoundingClientRect();
          if (pr.height > 0 && pr.bottom > bottom) bottom = pr.bottom;
        });
      }
      if (bottom > b) b = bottom;
    });
    return b;
  }

  wwcPlaceResults(container) {
    const keep = this.wwcKeepScroll;
    this.wwcKeepScroll = false;
    const release = () => {
      if (this.wwcHeld) {
        container.style.minHeight = '';
        this.wwcHeld = false;
      }
    };
    const grid = document.getElementById('ProductGridWrapper') || container;
    const chips = document.getElementById('FacetFiltersActive');
    let target = grid;
    if (chips && chips.offsetHeight > 0 && chips.textContent.trim() && chips.getBoundingClientRect().top < grid.getBoundingClientRect().top) target = chips;
    // let the sticky toolbar settle at its stuck position before measuring
    const off = Math.max(this.wwcStickyBottom(), 0) + 12;
    const top = target.getBoundingClientRect().top;
    const list = document.getElementById('ProductsList');
    const last = list && list.lastElementChild;
    const contentBottom = (last || container).getBoundingClientRect().bottom;
    let move = top < off - 2; // the visitor had scrolled past the top of the results
    if (keep && contentBottom > off + 120) move = false; // desktop sidebar: stay put while there are results on screen
    if (!move) {
      release();
      return;
    }
    const drawer = document.getElementById('FacetDrawer');
    const covered = !!(drawer && drawer.hasAttribute('open'));
    const smooth = !covered && !FoxTheme.config.motionReduced;
    window.scrollTo({ top: Math.max(0, window.scrollY + top - off), behavior: smooth ? 'smooth' : 'auto' });
    if (!smooth) {
      release();
      return;
    }
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      window.removeEventListener('scrollend', finish);
      release();
    };
    window.addEventListener('scrollend', finish);
    setTimeout(finish, 900);
  }

  renderSection(url, event) {
    const filterUpdate = this.startFilterUpdate(url);

    this.cachedMap.has(url)
      ? this.renderSectionFromCache(url, event, filterUpdate)
      : this.renderSectionFromFetch(url, event, filterUpdate);

    if (this.hasAttribute('data-history')) this.updateURLHash(url);

    this.dirty = false;
  }

  /**
   * Dispatches shopify:collection:update or shopify:search:update as the filtered results are requested.
   * Resolves with the number of results once they render.
   */
  startFilterUpdate(url) {
    const library = FoxTheme.storefrontEvents?.library;
    if (!library) return null;

    const params = new URL(url).searchParams;
    const parseOptions = { locale: window.Shopify?.locale };
    const { pageType, collectionId, collectionHandle, productsCount } = this.dataset;

    let filterUpdate = null;
    if (pageType === 'search') {
      filterUpdate = FoxTheme.storefrontEvents.start(this, 'SearchUpdateEvent', {
        search: {
          query: params.get('q') ?? new URLSearchParams(window.location.search).get('q') ?? '',
          productFilters: library.SearchUpdateEvent.parseProductFilters(params, parseOptions),
          sortKey: library.SearchUpdateEvent.getSortKey(params)
        }
      });
    } else if (pageType === 'collection') {
      filterUpdate = FoxTheme.storefrontEvents.start(this, 'CollectionUpdateEvent', {
        // Built-in collections such as /collections/all have no id.
        collection: { id: collectionId || null, handle: collectionHandle, productsCount: Number(productsCount) },
        productFilters: library.CollectionUpdateEvent.parseProductFilters(params, parseOptions),
        sortKey: library.CollectionUpdateEvent.getSortKey(params)
      });
    }
    if (!filterUpdate) return null;

    return {
      resolve: (responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const count = Number(html.getElementById('ProductGridContainer')?.dataset.productsCount) || 0;
        filterUpdate.resolve(pageType === 'search' ? { totalCount: count } : { productsCount: count });
      },
      reject: (error) => filterUpdate.reject(error)
    };
  }

  renderSectionFromFetch(url, event, filterUpdate) {
    this.beforeRenderSection();
    const start = performance.now();

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const execution = performance.now() - start;

        setTimeout(
          () => {
            try {
              this.renderFilters(responseText, event);
              this.renderFiltersActive(responseText);
              this.renderProductGridContainer(responseText);
              this.renderFacetFiltersContainer(responseText);
              this.renderProductCount(responseText);
              this.renderSortBy(responseText);
              this.renderSortByMobile(responseText);

              FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate, { responseText: responseText });
              this.cachedMap.set(url, responseText);

              filterUpdate?.resolve(responseText);
            } catch (error) {
              // Settle the standard event even when rendering fails, so its listeners don't wait forever.
              filterUpdate?.reject(error);
              throw error;
            }

            this.afterRenderSection();
          },
          execution > 250 ? 0 : 250
        );
      })
      .catch((error) => {
        filterUpdate?.reject(error);
        console.error(error);
      });
  }

  renderSectionFromCache(url, event, filterUpdate) {
    this.beforeRenderSection();

    setTimeout(() => {
      const responseText = this.cachedMap.get(url);
      try {
        this.renderFilters(responseText, event);
        this.renderFiltersActive(responseText);
        this.renderProductGridContainer(responseText);
        this.renderFacetFiltersContainer(responseText);
        this.renderProductCount(responseText);
        this.renderSortBy(responseText);
        this.renderSortByMobile(responseText);

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate, { responseText: responseText });

        filterUpdate?.resolve(responseText);
      } catch (error) {
        // Settle the standard event even when rendering fails, so its listeners don't wait forever.
        filterUpdate?.reject(error);
        throw error;
      }

      this.afterRenderSection();
    }, 250);
  }

  renderFilters(responseText, event) {
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const facetElements = parsedHTML.querySelectorAll(
      '#FacetFiltersContainer [data-filter], #MobileFacetFiltersContainer [data-filter], #DrawerFacetFiltersContainer [data-filter]'
    );

    const facetElementsFormDom = document.querySelectorAll(
      '#FacetFiltersContainer [data-filter], #MobileFacetFiltersContainer [data-filter], #DrawerFacetFiltersContainer [data-filter]'
    );

    Array.from(facetElementsFormDom).forEach((currentElement) => {
      if (!Array.from(facetElements).some(({ id }) => currentElement.id === id)) {
        currentElement.classList.add('hidden');
      }
    });

    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('[data-filter]') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetElements).filter((element) => !matchesIndex(element));

    facetsToRender.forEach((element) => {
      const filter = document.querySelector(`[data-filter][data-index="${element.dataset.index}"]`);
      if (filter !== null) {
        filter.classList.remove('hidden');
        if (filter.tagName === 'DETAILS') {
          filter.querySelector('summary + *').innerHTML = element.querySelector('summary + *').innerHTML;
        } else {
          filter.innerHTML = element.innerHTML;
        }
      }
    });
  }

  renderFiltersActive(responseText) {
    const id = 'FacetFiltersActive';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }

  renderProductGridContainer(responseText) {
    const id = 'ProductGridContainer';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;

    const layoutSwitcher = document.querySelector('layout-switcher');
    if (layoutSwitcher) layoutSwitcher.onButtonClick(layoutSwitcher.querySelector('.btn--active'));
  }

  renderFacetFiltersContainer(responseText) {
    const id = 'FacetFiltersContainer';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    // WWC D115: the sidebar is rebuilt on every change - keep its inner scroll position and which groups are open, so the
    // panel doesn't snap back to the top (or collapse the group just used) after each tick.
    const wwcEl = document.getElementById(id);
    const wwcScroller = wwcEl.querySelector(':scope > .sticky-element');
    const wwcTop = wwcScroller ? wwcScroller.scrollTop : 0;
    const wwcOpen = {};
    wwcEl.querySelectorAll('details[data-index]').forEach((d) => { wwcOpen[d.dataset.index] = d.open; });

    wwcEl.innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;

    wwcEl.querySelectorAll('details[data-index]').forEach((d) => {
      if (d.dataset.index in wwcOpen) d.open = wwcOpen[d.dataset.index];
    });
    const wwcNewScroller = wwcEl.querySelector(':scope > .sticky-element');
    if (wwcNewScroller && wwcTop) wwcNewScroller.scrollTop = wwcTop;
  }

  renderProductCount(responseText) {
    const id = 'ProductCount';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }

  renderSortBy(responseText) {
    const id = 'SortByContainer';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }

  renderSortByMobile(responseText) {
    const id = 'SortByContainerMobile';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }
}
customElements.define('facet-form', FacetForm, { extends: 'form' });

class FacetRemove extends HTMLAnchorElement {
  constructor() {
    super();
    this.addEventListener('click', this.onClick);
  }

  onClick(event) {
    const form = this.closest('form[is="facet-form"]') || document.querySelector('form[is="facet-form"]');

    if (form) {
      event.preventDefault();

      const url = new URL(this.href);
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(form));
      form.renderSection(url.toString(), event);
    }
  }
}
customElements.define('facet-remove', FacetRemove, { extends: 'a' });

class FacetCount extends HTMLElement {
  constructor() {
    super();
  }

  facetUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.facetUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate,
      this.onFacetUpdate.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.facetUpdateUnsubscriber) {
      this.facetUpdateUnsubscriber();
    }
  }

  get itemCount() {
    return parseInt(this.innerText.replace(/\D/g, ''));
  }

  onFacetUpdate(event) {
    const parsedHTML = new DOMParser().parseFromString(event.responseText, 'text/html');
    const facetCount = parsedHTML.querySelector('facet-count');
    this.innerText = facetCount && facetCount.innerHTML;
    this.hidden = this.itemCount === 0;
  }
}
customElements.define('facet-count', FacetCount);

class FacetToggler extends HTMLButtonElement {
  constructor() {
    super();
    this.onClickHandler = this.onClick.bind(this);
  }

  connectedCallback() {
    this.facetEl = document.getElementById('FacetFiltersContainer');
    this.addEventListener('click', this.onClickHandler);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClickHandler);
  }

  onClick(evt) {
    evt.preventDefault();

    if (this.facetEl.classList.contains('xl:block')) {
      this.facetEl.classList.remove('xl:block');
    } else {
      this.facetEl.classList.add('xl:block');
    }
  }
}
customElements.define('facet-toggler', FacetToggler, { extends: 'button' });

class LoadMoreButton extends HTMLButtonElement {
  constructor() {
    super();
    this.onClickHandler = this.onClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.onClickHandler);

    if (this.getAttribute('type') == 'infinite') {
      FoxTheme.Motion.inView(this, this.onClickHandler, { margin: '200px 0px 200px 0px' });
    }
    // WWC D61: infinite scroll in batches of the page size, max N batches per "screen", then a real 'Next page' link
    if (this.hasAttribute('data-wwc-auto')) this.setupAuto();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClickHandler);
    if (this.wwcIO) this.wwcIO.disconnect();
  }

  get wwcNav() {
    return this.closest('.pagination');
  }

  // batches shown on this screen so far = page of the last batch - first page of the screen + 1
  wwcLoaded() {
    const nav = this.wwcNav;
    const start = nav ? parseInt(nav.dataset.wwcStart, 10) || 1 : 1;
    return (parseInt(this.dataset.page, 10) || 1) - start + 1;
  }

  setupAuto() {
    // a Back-navigation rebuild is pending (WwcInfinite.restore): don't auto-load until the earlier batches are back
    if (window.__wwcRestoring === undefined) window.__wwcRestoring = !!wwcRestoreRecord();
    if (window.__wwcRestoring) return;
    const nav = this.wwcNav;
    const max = nav ? parseInt(nav.dataset.wwcBatches, 10) || 4 : 4;
    if (this.wwcLoaded() >= max) {
      // screen is full: stop auto-loading so the footer stays reachable; the real link goes to the next page
      this.hidden = true;
      const next = nav && nav.querySelector('.wwc-lm__next');
      if (next) next.hidden = false;
      return;
    }
    if (!('IntersectionObserver' in window)) return; // manual button stays
    this.classList.add('wwc-lm__btn--auto');
    this.wwcIO = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          this.wwcIO.disconnect();
          this.onClick();
        }
      },
      { rootMargin: '0px 0px 600px 0px' }
    );
    this.wwcIO.observe(nav || this); // the button itself is display:none while auto-loading, so watch the pagination block
  }

  onClick() {
    if (this.classList.contains('btn--loading')) return;
    this.loadingState();
    const nav = this.wwcNav;
    if (nav) nav.classList.add('is-loading');

    const url = this.setUrl().toString();

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((responseText) => {
        const list = document.querySelector('#ProductGridContainer .products-list');
        const before = list ? list.children.length : 0;
        this.renderPagination(responseText);
        this.renderProductGridContainer(responseText);
        if (nav) nav.classList.remove('is-loading');
        // WWC D60: keep ?page in the address bar (shareable / back button) and move focus to the first new product
        try {
          const u = new URL(url);
          u.searchParams.delete('section_id');
          history.replaceState(history.state, '', u.pathname + u.search);
          WwcInfinite.remember(nav);
        } catch (e) {}
        // D61: focus only follows a manual click (auto-loaded batches must not move focus/scroll)
        if (!this.classList.contains('wwc-lm__btn--auto')) {
          const first = list && list.children[before] && list.children[before].querySelector('a[href]:not([tabindex="-1"])');
          if (first) first.focus({ preventScroll: true });
        }
      })
      .catch(() => {
        // failed fetch: leave the manual button usable
        this.classList.remove('pointer-events-none', 'btn--loading', 'wwc-lm__btn--auto');
        if (nav) nav.classList.remove('is-loading');
      });
  }

  renderPagination(responseText) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer === null) return;

    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const pagination = productGridContainer.querySelector('.pagination');
    const source = parsedHTML.querySelector('.pagination');

    if (source) {
      pagination.innerHTML = source.innerHTML;
    } else {
      pagination.remove();
    }
  }

  renderProductGridContainer(responseText) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer === null) return;

    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const productList = productGridContainer.querySelector('.products-list');
    const source = parsedHTML.querySelector('.products-list');

    if (source && productList) {
      source.querySelectorAll('.f-column').forEach((item) => {
        productList.appendChild(item);
      });
    }
  }

  setUrl() {
    const url = new URL(this.getAttribute('action'));
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url;
  }

  loadingState() {
    this.classList.add('pointer-events-none');
    this.classList.add('btn--loading');
  }
}
customElements.define('load-more-button', LoadMoreButton, { extends: 'button' });

/* WWC D61: infinite-scroll helpers.
   remember(): after each batch, store {start, last} for this listing (URL without page) so Back can rebuild the screen.
   restore(): on a Back/Forward load (not bfcache) of ?page=N where the shopper had batches start..N on screen, refetch pages
   start..N-1 through the Section Rendering API, put them back above page N and restore the scroll position. */
function wwcRestoreKey() {
  const u = new URL(location.href);
  u.searchParams.delete('page');
  return 'wwcis:' + u.pathname + '?' + u.searchParams.toString();
}
// record of the screen to rebuild on a Back/Forward load (not bfcache), or null
function wwcRestoreRecord() {
  try {
    const nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
    if (!nav || nav.type !== 'back_forward') return null;
    const r = JSON.parse(sessionStorage.getItem(wwcRestoreKey()) || 'null');
    const page = parseInt(new URL(location.href).searchParams.get('page'), 10) || 1;
    if (!r || r.last !== page || r.start >= page || Date.now() - r.t > 1800000) return null;
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    return r;
  } catch (e) {
    return null;
  }
}
const WwcInfinite = {
  key() {
    return wwcRestoreKey();
  },
  remember(nav) {
    try {
      const start = nav ? parseInt(nav.dataset.wwcStart, 10) || 1 : 1;
      const last = parseInt(new URL(location.href).searchParams.get('page'), 10) || 1;
      sessionStorage.setItem(this.key(), JSON.stringify({ start, last, y: window.scrollY, t: Date.now() }));
    } catch (e) {}
  },
  saveScroll() {
    try {
      const r = JSON.parse(sessionStorage.getItem(this.key()) || 'null');
      if (r) { r.y = window.scrollY; sessionStorage.setItem(this.key(), JSON.stringify(r)); }
    } catch (e) {}
  },
  done() {
    window.__wwcRestoring = false;
    const btn = document.querySelector('#ProductGridContainer .pagination button[data-wwc-auto]');
    if (btn) { if (btn.wwcIO) btn.wwcIO.disconnect(); btn.setupAuto(); }
  },
  restore() {
    const r = wwcRestoreRecord();
    const nav = document.querySelector('#ProductGridContainer .pagination[data-wwc-start]');
    const list = document.querySelector('#ProductGridContainer .products-list');
    const page = parseInt(new URL(location.href).searchParams.get('page'), 10) || 1;
    if (!r || !nav || !list) { if (window.__wwcRestoring) this.done(); return; }
    window.__wwcRestoring = true;
    const sid = FoxTheme.utils.getSectionId(list);
    const pages = [];
    for (let p = r.start; p < page; p++) pages.push(p);
    Promise.all(pages.map((p) => {
      const u = new URL(location.href);
      u.searchParams.set('page', p);
      u.searchParams.set('section_id', sid);
      return fetch(u.toString()).then((res) => res.text());
    })).then((htmls) => {
      const frag = document.createDocumentFragment();
      htmls.forEach((h) => {
        const src = new DOMParser().parseFromString(h, 'text/html').querySelector('.products-list');
        if (src) src.querySelectorAll('.f-column').forEach((it) => frag.appendChild(it));
      });
      list.insertBefore(frag, list.firstChild);
      nav.dataset.wwcStart = r.start;
      const prev = document.querySelector('#ProductGridContainer .wwc-lm__prev');
      if (prev) {
        if (r.start > 1) { const u = new URL(location.href); u.searchParams.set('page', r.start - 1); prev.href = u.pathname + u.search; } else prev.remove();
      }
      window.scrollTo(0, r.y || 0);
      // re-evaluate the batch limit with the restored start (after the scroll, so it can't fire early)
      requestAnimationFrame(() => this.done());
    }).catch(() => this.done());
  }
};
window.WwcInfinite = WwcInfinite;
window.addEventListener('pagehide', () => WwcInfinite.saveScroll());
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => WwcInfinite.restore());
else WwcInfinite.restore();

class LayoutSwitcher extends HTMLElement {
  constructor() {
    super();
    this.cookieName = 'hypertheme:collection-layout';

    this.initLayoutMode();
    this.buttons.forEach((button) => {
      button.addEventListener('click', this.onButtonClick.bind(this));
    });
  }

  get buttons() {
    return this.querySelectorAll('button');
  }

  /**
   * @param {event|element} evt
   */
  onButtonClick(evt) {
    const target = evt.currentTarget ? evt.currentTarget : evt;
    this.changeLayout(target, target.dataset.layoutMode);
  }

  initLayoutMode() {
    if (FoxTheme.config.hasLocalStorage) {
      const layoutMode = window.localStorage.getItem(this.cookieName);

      if (layoutMode !== null) {
        const target = this.querySelector(`button[data-layout-mode="${layoutMode}"]`);
        if (target) {
          this.changeLayout(target, layoutMode);
        }
      }
    }
  }

  changeLayout(target, layoutMode) {
    const productGrid = document.getElementById('ProductsList');
    if (!productGrid) return;

    productGrid.dataset.layout = layoutMode;

    const { gridClass, listClass } = productGrid.dataset;
    if ((gridClass || listClass) && gridClass !== listClass) {
      const productCards = productGrid.querySelectorAll('.product-card');
      productCards &&
        productCards.forEach((card) => {
          if ('grid' === layoutMode) {
            listClass && card.classList.remove(listClass);
            gridClass && card.classList.add(gridClass);
          } else {
            gridClass && card.classList.remove(gridClass);
            listClass && card.classList.add(listClass);
          }
        });
    }

    this.buttons.forEach((button) => {
      button.classList.remove('btn--active');
    });
    target.classList.add('btn--active');

    if (FoxTheme.config.hasLocalStorage) {
      window.localStorage.setItem(this.cookieName, layoutMode);
    }
  }
}
customElements.define('layout-switcher', LayoutSwitcher);
